import React from 'react';
import { useLocation } from 'react-router-dom';
import { useContactForm } from '@/contexts/ContactFormContext';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { normalizeTrackingName, trackCtaClick } from '@/lib/utils/dataLayer';
import {
  setPendingLeadAttribution,
  type OfferType,
} from '@/lib/utils/leadAttribution';

interface ContactFormButtonProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
  icon?: boolean;
  service?: string | string[];
  ctaName?: string;
  ctaLocation?: string;
  offerType?: OfferType;
  serviceContext?: string;
  formVariant?: string;
  onClick?: () => void; // Additional onClick handler
}

const ContactFormButton = ({
  children,
  className,
  variant = 'primary',
  size = 'default',
  icon = true,
  service,
  ctaName,
  ctaLocation,
  offerType = 'consultation',
  serviceContext,
  formVariant = 'multi_step_contact_form',
  onClick,
}: ContactFormButtonProps) => {
  const location = useLocation();
  const { openContactForm } = useContactForm();
  const isHomePage = location.pathname === '/';

  const handleClick = () => {
    const buttonText =
      typeof children === 'string' ? children : 'contact_form_cta';
    const normalizedCtaName = ctaName || normalizeTrackingName(buttonText);
    const normalizedCtaLocation =
      ctaLocation || `contact_form_button:${location.pathname}`;
    const services = service
      ? Array.isArray(service)
        ? service
        : [service]
      : [];
    const normalizedServiceContext =
      serviceContext || services[0]?.toLowerCase() || 'general';

    setPendingLeadAttribution({
      cta_name: normalizedCtaName,
      cta_location: normalizedCtaLocation,
      offer_type: offerType,
      service_context: normalizedServiceContext,
      form_variant: formVariant,
    });

    trackCtaClick(normalizedCtaName, normalizedCtaLocation, {
      destination: isHomePage ? 'contact_form_section' : 'contact_form_modal',
      services,
      offer_type: offerType,
      service_context: normalizedServiceContext,
      form_variant: formVariant,
    });

    // Call the additional onClick handler if provided (e.g., to close mobile menu)
    if (onClick) {
      onClick();
    }

    if (isHomePage) {
      // Scroll to contact form on home page
      const contactSection = document.getElementById('contact-form');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Open dialog on other pages
      // Convert single service to array if provided
      const servicesToSelect = service
        ? Array.isArray(service)
          ? service
          : [service]
        : [];
      openContactForm(servicesToSelect);
    }
  };

  const buttonClass = cn(
    'group',
    variant === 'primary' ? 'btn-primary' : 'btn-secondary',
    size === 'sm' && 'text-sm px-4 py-2',
    size === 'lg' && 'text-lg px-8 py-4',
    className
  );

  return (
    <button onClick={handleClick} className={buttonClass}>
      {children}
      {icon && (
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      )}
    </button>
  );
};

export default ContactFormButton;
